import { Fragment, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { ApiError } from '../../api/client.js';
import type { Prize, PrizeInput, Product } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, SafeLink } from '../../components/ui.js';
import { lotteryGradeGradient } from '../../components/LotteryGradeBadge.js';
import { text, money, num, datetime, datetimeWithSeconds, dateOnly, isZeroDate } from '../../lib/format.js';
import {
  checkImageUrl,
  checkLen,
  checkProbability,
  checkRequired,
  hasFieldErrors,
  inputCls,
  type FieldErrors,
} from '../../lib/formValidation.js';
import { ProductFormModal } from './ProductsPage.js';

const GRADES = ['A', 'B', 'C', 'D', 'E', 'L'];

/** 원본 prize_list.php 의 '이미지URL' 칸은 이 접두사를 떼고 보여주고, 저장할 때 다시 붙인다. */
const CDN_PREFIX = 'https://cdn-banana.bizhost.kr/files/lottery/';

/**
 * 행운상 시작·종료 시각. 원본 prize_modify.php 의 lucky_start_hour(00/18)와
 * lucky_end_hour(17/23, 항상 59분)를 그대로 옮겼다. 실데이터 행운상 267건은 전부
 * 18:00 시작, 17:59 또는 23:59 종료다 (2026-11-04).
 */
const LUCKY_START_TIMES = ['00:00', '18:00'];
const LUCKY_END_TIMES = ['17:59', '23:59'];

/**
 * 원본: lottery/product/prize_list.php + prize_modify.php + prize_copy.php
 *       + prize_image_modify.php + product_detail_add.php + prize_history.php
 *
 * 2026-11-04 원본 상세 화면과 같은 표로 되돌렸다 (상품 한 줄 표 + 세부 랜덤박스 표,
 * 행운상은 두 줄째에 시작일~종료일). 그 전 화면은 행운상 수정 모달에서 '당첨용
 * 이미지 URL' 줄과 '행운상 타이틀/시작일/종료일' 줄이 겹쳐 그려졌고, 저장하면
 * 행운상 시각(18:00·17:59)이 00:00 으로 바뀌었으며, 확률 8.20·0.10 은 정수가
 * 아니라서 저장이 막혔다.
 *
 * 원본과 다른 점: 수량은 목록에서 바로 고친다 (서버가 compare-and-set 을 해서, 그
 * 사이 다른 관리자가 바꿨으면 409 로 알려준다). 설명은 줄바꿈을 살려 보여준다.
 */
export function PrizesPage() {
  const { lotteryId: raw = '' } = useParams();
  const lotteryId = Number(raw);
  const queryClient = useQueryClient();

  const [historyFor, setHistoryFor] = useState<number | null>(null);
  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy' | 'add'; prize: Prize } | null>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);

  const validId = Number.isFinite(lotteryId) && lotteryId > 0;

  const product = useQuery({
    queryKey: ['lottery', 'product', lotteryId],
    queryFn: () => lotteryApi.product(lotteryId),
    enabled: validId,
  });

  const prizes = useQuery({
    queryKey: ['lottery', 'prizes', lotteryId],
    queryFn: () => lotteryApi.prizes(lotteryId),
    enabled: validId,
  });

  // 랜덤박스 수정 모달의 카테고리 선택지. 목록 화면과 같은 키라 캐시를 같이 쓴다.
  const categories = useQuery({
    queryKey: ['lottery', 'categories', 'all'],
    queryFn: () => lotteryApi.categories({ page: 1, perPage: 200 }),
    enabled: productModalOpen,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['lottery', 'prizes', lotteryId] });
    // 경품을 고치면 서버가 상품 총확률(drawProbability)을 다시 계산한다.
    void queryClient.invalidateQueries({ queryKey: ['lottery', 'product', lotteryId] });
  }

  const remove = useMutation({
    mutationFn: (prizeId: number) => lotteryApi.deletePrize(lotteryId, prizeId),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  if (!validId) {
    return <ErrorBox error={new Error('잘못된 랜덤박스 ID 입니다.')} />;
  }

  const prizeItems = prizes.data?.items ?? [];
  const totalProbability = prizeItems
    .filter((prize) => prize.grade !== 'L')
    .reduce((sum, prize) => sum + (prize.drawProbability ?? 0), 0);

  return (
    <section className="lottery-prize-page lottery-legacy">
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/lottery/products">
            ← 랜덤박스 목록
          </Link>
          <h1 className="page-title lottery-legacy__title">랜덤박스 상세페이지</h1>
        </div>
      </header>

      {product.isPending ? (
        <Spinner />
      ) : product.isError ? (
        <ErrorBox error={product.error} onRetry={() => void product.refetch()} />
      ) : (
        <ProductTable
          product={product.data}
          lotteryId={lotteryId}
          onEdit={() => setProductModalOpen(true)}
          onAddPrize={() => setFormModal({ mode: 'add', prize: emptyPrize(lotteryId) })}
        />
      )}

      <div className="lottery-legacy__bar">세부 랜덤박스</div>

      {prizes.isPending ? (
        <Spinner />
      ) : prizes.isError ? (
        <ErrorBox error={prizes.error} onRetry={() => void prizes.refetch()} />
      ) : prizeItems.length === 0 ? (
        <EmptyBox>등록된 경품이 없습니다.</EmptyBox>
      ) : (
        <div className="table-wrap lottery-legacy__wrap">
          <table className="table lottery-legacy-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>랜덤박스등급</th>
                <th>이미지</th>
                <th>확률(%)</th>
                <th>상품코드</th>
                <th>설명</th>
                <th>수량</th>
                <th>이미지URL</th>
                <th>등록일</th>
                <th>수정</th>
                <th>복사</th>
                <th>삭제</th>
                <th>변경이력</th>
              </tr>
            </thead>
            <tbody>
              {prizeItems.map((prize, i) => (
                <PrizeRow
                  key={prize.prizeId ?? i}
                  index={i + 1}
                  lotteryId={lotteryId}
                  prize={prize}
                  confirmingDelete={confirmingDelete === prize.prizeId}
                  onShowHistory={() => setHistoryFor(prize.prizeId ?? null)}
                  onEdit={() => setFormModal({ mode: 'edit', prize })}
                  onCopy={() => setFormModal({ mode: 'copy', prize })}
                  onAskDelete={() => setConfirmingDelete(prize.prizeId)}
                  onCancelDelete={() => setConfirmingDelete(null)}
                  onConfirmDelete={() => {
                    if (prize.prizeId !== null) remove.mutate(prize.prizeId);
                  }}
                  deletePending={remove.isPending}
                  onSaved={refresh}
                />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>총 확률</td>
                <td colSpan={10}>{num(Math.round(totalProbability * 100) / 100)}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {remove.isError && (
        <div className="qty-msg qty-msg--error">
          {remove.error instanceof Error ? remove.error.message : '삭제에 실패했습니다.'}
        </div>
      )}

      {historyFor !== null && (
        <HistoryPanel lotteryId={lotteryId} prizeId={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      {formModal && (
        <PrizeFormModal
          mode={formModal.mode}
          lotteryId={lotteryId}
          productName={product.data?.name ?? null}
          prize={formModal.prize}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}

      {productModalOpen && product.data && (categories.isSuccess || categories.isError) && (
        <ProductFormModal
          mode="edit"
          product={product.data}
          categories={categories.data?.items ?? []}
          onClose={() => setProductModalOpen(false)}
          onRefresh={() => {
            void queryClient.invalidateQueries({ queryKey: ['lottery', 'product', lotteryId] });
            void queryClient.invalidateQueries({ queryKey: ['lottery', 'products'] });
          }}
        />
      )}
    </section>
  );
}

function emptyPrize(lotteryId: number): Prize {
  return {
    prizeId: null,
    lotteryId,
    prizeCode: null,
    grade: 'A',
    name: null,
    description: null,
    quantity: null,
    drawProbability: null,
    price: null,
    thumbnail: null,
    imageUrl: null,
    luckyTitle: null,
    luckyStartDate: null,
    luckyEndDate: null,
    deliveryDate: null,
    memo: null,
    regdate: null,
  };
}

/** 원본 prize_list.php:401 - item_type 2(오나홀)는 fanza_kuji_hole, 나머지는 fanza_kuji_detail. */
function shopUrl(product: Product, lotteryId: number): string {
  const page = product.itemType === '2' ? 'fanza_kuji_hole' : 'fanza_kuji_detail';
  return `https://www.bananamall.co.kr/etc/${page}.php?kuji=${lotteryId}`;
}

/** 원본 prize_list.php:364-444 의 상품 한 줄 표. */
function ProductTable({
  product,
  lotteryId,
  onEdit,
  onAddPrize,
}: {
  product: Product;
  lotteryId: number;
  onEdit: () => void;
  onAddPrize: () => void;
}) {
  return (
    <div className="table-wrap lottery-legacy__wrap">
      <table className="table lottery-legacy-product">
        <thead>
          <tr>
            <th>랜덤박스 번호</th>
            <th>랜덤박스명</th>
            <th>썸네일</th>
            <th>판매가격</th>
            <th>기간</th>
            <th>발송 예정일</th>
            <th>관리용 메모</th>
            <th>등록일</th>
            <th>수정</th>
            <th>상품 추가</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <a className="lottery-legacy-product__id" href={shopUrl(product, lotteryId)} target="_blank" rel="noreferrer">
                {lotteryId}
              </a>
            </td>
            <td className="lottery-legacy-product__name">{text(product.name)}</td>
            <td>
              {product.thumbnail ? (
                <SafeLink href={product.thumbnail} title="원본 이미지 열기">
                  <img className="lottery-legacy-product__thumb" src={product.thumbnail} alt={product.name ?? ''} />
                </SafeLink>
              ) : (
                '-'
              )}
            </td>
            <td className="lottery-legacy-product__price">{product.price === null ? '-' : `${money(product.price)}원`}</td>
            <td>
              <span className="lottery-legacy-product__period">
                {dateOnly(product.saleStart)} ~ {dateOnly(product.saleEnd)}
              </span>
            </td>
            <td>{text(product.deliveryDate)}</td>
            <td>
              <textarea
                className="lottery-legacy-product__memo"
                rows={2}
                readOnly
                aria-label="관리용 메모"
                value={product.memo ?? ''}
              />
            </td>
            <td className="lottery-legacy-product__date">{dateOnly(product.regdate)}</td>
            <td>
              <button type="button" className="btn btn--sm lottery-legacy-product__edit" onClick={onEdit}>
                수정
              </button>
            </td>
            <td>
              <button type="button" className="btn btn--sm lottery-legacy-product__add" onClick={onAddPrize}>
                상품 추가
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** 원본은 확률을 DB 값(decimal(5,2)) 그대로 찍는다 - 8.20, 0.10. */
function probabilityText(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : value.toFixed(2);
}

function PrizeRow(props: {
  index: number;
  lotteryId: number;
  prize: Prize;
  confirmingDelete: boolean;
  onShowHistory: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  deletePending: boolean;
  onSaved: () => void;
}) {
  const { prize } = props;
  const isLucky = prize.grade === 'L';
  // 원본은 행운상이면 설명 칸만 빼고 rowspan=2 로 두고, 둘째 줄에 기간을 적는다.
  const span = isLucky ? 2 : undefined;

  return (
    <Fragment>
      <tr className={isLucky ? 'lottery-legacy-table__lucky' : undefined}>
        <td rowSpan={span} className="td-muted">{props.index}</td>
        <td rowSpan={span} className="lottery-legacy-table__grade" style={{ background: lotteryGradeGradient(prize.grade) }}>
          {isLucky ? '행운' : text(prize.grade)}상
        </td>
        <td rowSpan={span} className="lottery-legacy-table__image-cell">
          {prize.thumbnail ? (
            <SafeLink href={prize.thumbnail} title="원본 이미지 열기">
              <img className="lottery-legacy-table__image" src={prize.thumbnail} alt="" loading="lazy" />
            </SafeLink>
          ) : (
            <span className="td-muted">NO IMAGE</span>
          )}
        </td>
        <td rowSpan={span} className="lottery-legacy-table__probability">{probabilityText(prize.drawProbability)}</td>
        <td rowSpan={span}><code className="lottery-legacy-table__code">{text(prize.prizeCode)}</code></td>
        <td className="lottery-legacy-table__description">{text(prize.description)}</td>
        <td rowSpan={span} className="lottery-legacy-table__quantity">
          <QuantityEditor lotteryId={props.lotteryId} prize={prize} onSaved={props.onSaved} />
        </td>
        <td rowSpan={span} className="lottery-legacy-table__thumb-cell">
          {/* 저장 뒤 다시 불러온 주소로 입력칸을 새로 그린다. */}
          <ThumbnailEditor key={prize.thumbnail ?? ''} lotteryId={props.lotteryId} prize={prize} onSaved={props.onSaved} />
        </td>
        <td rowSpan={span} className="lottery-legacy-table__date">{datetimeWithSeconds(prize.regdate)}</td>
        <td rowSpan={span}>
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--edit" onClick={props.onEdit}>
            수정
          </button>
        </td>
        <td rowSpan={span}>
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--copy" onClick={props.onCopy}>
            복사
          </button>
        </td>
        <td rowSpan={span}>
          {props.confirmingDelete ? (
            <div className="lottery-legacy-table__confirm">
              <button
                type="button"
                className="btn btn--sm lottery-prize-action lottery-prize-action--delete"
                disabled={props.deletePending}
                onClick={props.onConfirmDelete}
              >
                정말 삭제
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={props.onCancelDelete}>
                취소
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--delete" onClick={props.onAskDelete}>
              삭제
            </button>
          )}
        </td>
        <td rowSpan={span}>
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--history" onClick={props.onShowHistory}>
            변경이력
          </button>
        </td>
      </tr>
      {isLucky && (
        <tr className="lottery-legacy-table__lucky-period">
          <td>
            시작일: {datetimeWithSeconds(prize.luckyStartDate)}&nbsp;~&nbsp;종료일: {datetimeWithSeconds(prize.luckyEndDate)}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function QuantityEditor({ lotteryId, prize, onSaved }: { lotteryId: number; prize: Prize; onSaved: () => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const current = prize.quantity ?? 0;
  const editing = draft !== null;

  const mutation = useMutation({
    mutationFn: (quantity: number) => {
      if (prize.prizeId === null) throw new Error('경품 ID 가 없습니다.');
      return lotteryApi.updatePrizeQuantity(lotteryId, prize.prizeId, quantity);
    },
    onSuccess: () => {
      setDraft(null);
      onSaved();
    },
  });

  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409;

  return (
    <>
      <div className="qty">
        <input
          className="input input--qty"
          type="number"
          min={0}
          aria-label={`${text(prize.prizeCode)} 수량`}
          value={editing ? draft : String(current)}
          onChange={(e) => setDraft(e.target.value)}
          disabled={mutation.isPending || prize.prizeId === null}
        />
        {editing && (
          <>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={mutation.isPending}
              onClick={() => {
                const value = Number(draft);
                if (!Number.isFinite(value) || value < 0) return;
                mutation.mutate(value);
              }}
            >
              {mutation.isPending ? '…' : '저장'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraft(null)}>
              취소
            </button>
          </>
        )}
      </div>
      {mutation.isError && (
        <div className={conflict ? 'qty-msg qty-msg--conflict' : 'qty-msg qty-msg--error'}>
          {mutation.error instanceof Error ? mutation.error.message : '저장 실패'}
          {conflict && (
            <button type="button" className="link" onClick={() => { setDraft(null); onSaved(); }}>
              새로고침
            </button>
          )}
        </div>
      )}
    </>
  );
}

function stripCdnPrefix(url: string | null): string {
  if (!url) return '';
  return url.startsWith(CDN_PREFIX) ? url.slice(CDN_PREFIX.length) : url;
}

/** 원본은 무조건 접두사를 붙였다. 완성된 주소를 붙여 넣으면 그대로 둔다. */
function withCdnPrefix(value: string): string {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : CDN_PREFIX + trimmed.replace(/^\/+/, '');
}

/** 원본 prize_list.php:526-552 의 '이미지URL' 칸 (prize_image_modify.php). */
function ThumbnailEditor({ lotteryId, prize, onSaved }: { lotteryId: number; prize: Prize; onSaved: () => void }) {
  const [value, setValue] = useState(() => stripCdnPrefix(prize.thumbnail));
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      if (prize.prizeId === null) throw new Error('경품 ID 가 없습니다.');
      return lotteryApi.updatePrizeThumbnail(lotteryId, prize.prizeId, withCdnPrefix(value));
    },
    onSuccess: () => {
      setSaved(true);
      onSaved();
    },
  });

  return (
    <div className="lottery-legacy-thumb">
      <textarea
        className="lottery-legacy-thumb__input"
        aria-label={`${text(prize.prizeCode)} 이미지URL`}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setSaved(false);
        }}
      />
      <button
        type="button"
        className="btn btn--sm lottery-legacy-thumb__btn"
        aria-label="이미지URL 수정"
        disabled={save.isPending || value.trim() === '' || prize.prizeId === null}
        onClick={() => save.mutate()}
      >
        수정
      </button>
      {saved && <span className="lottery-legacy-thumb__ok" role="status">썸네일이 수정되었습니다.</span>}
      {save.isError && (
        <span className="qty-msg qty-msg--error">
          {save.error instanceof Error ? save.error.message : '저장 실패'}
        </span>
      )}
    </div>
  );
}

interface PrizeFormState {
  grade: string;
  prizeCode: string;
  quantity: string;
  drawProbability: string;
  thumbnail: string;
  imageUrl: string;
  luckyTitle: string;
  luckyStartDate: string;
  luckyStartTime: string;
  luckyEndDate: string;
  luckyEndTime: string;
  description: string;
}

/** 'YYYY-MM-DD HH:MM:SS' 를 날짜 칸과 시각 선택으로 나눈다. 값이 없으면 원본 기본값(18:00 / 17:59). */
function splitDateTime(value: string | null, fallbackTime: string): { date: string; time: string } {
  if (!value || isZeroDate(value)) return { date: '', time: fallbackTime };
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value);
  if (!m) return { date: '', time: fallbackTime };
  return { date: m[1]!, time: m[2] ? `${m[2]}:${m[3]}` : fallbackTime };
}

function joinDateTime(date: string, time: string): string {
  return date ? `${date} ${time}:00` : '';
}

function toPrizeFormState(p: Prize): PrizeFormState {
  const start = splitDateTime(p.luckyStartDate, '18:00');
  const end = splitDateTime(p.luckyEndDate, '17:59');
  return {
    grade: p.grade && GRADES.includes(p.grade) ? p.grade : GRADES[0]!,
    prizeCode: p.prizeCode ?? '',
    quantity: p.quantity !== null && p.quantity !== undefined ? String(p.quantity) : '',
    // DB 는 decimal(5,2) 다 - 목록처럼 0.10·8.20 으로 채운다 (String() 이면 0.1·8.2 가 된다).
    drawProbability:
      p.drawProbability !== null && p.drawProbability !== undefined ? p.drawProbability.toFixed(2) : '',
    thumbnail: p.thumbnail ?? '',
    imageUrl: p.imageUrl ?? '',
    luckyTitle: p.luckyTitle ?? '',
    luckyStartDate: start.date,
    luckyStartTime: start.time,
    luckyEndDate: end.date,
    luckyEndTime: end.time,
    description: p.description ?? '',
  };
}

function toPrizeInput(form: PrizeFormState): PrizeInput {
  return {
    grade: form.grade,
    prizeCode: form.prizeCode,
    quantity: Number(form.quantity) || 0,
    drawProbability: form.drawProbability === '' ? null : Number(form.drawProbability),
    thumbnail: form.thumbnail,
    imageUrl: form.imageUrl,
    luckyTitle: form.luckyTitle,
    luckyStartDate: joinDateTime(form.luckyStartDate, form.luckyStartTime),
    luckyEndDate: joinDateTime(form.luckyEndDate, form.luckyEndTime),
    description: form.description,
  };
}

/** 원본 선택지 + 지금 값. 원본에 없는 시각이 저장돼 있어도 열고 저장하는 것만으로 바뀌지 않게 한다. */
function timeOptions(base: string[], current: string): string[] {
  return base.includes(current) ? base : [...base, current].sort();
}

/**
 * 원본 prize_modify.php 의 표 한 줄 - 왼쪽 제목, 오른쪽 입력. 날짜+시각처럼 입력이
 * 둘 이상이면 as="div" 로 둔다 (<label> 은 안의 첫 입력에만 클릭을 넘긴다).
 */
function FormRow({
  label,
  error,
  as = 'label',
  children,
}: {
  label: string;
  error?: string | undefined;
  as?: 'label' | 'div';
  children: ReactNode;
}) {
  const Tag = as;
  return (
    <Tag className="lottery-prize-form__row">
      <span className="lottery-prize-form__label">{label}</span>
      <span className="lottery-prize-form__control">
        {children}
        {error ? (
          <span className="field__error" role="alert">
            {error}
          </span>
        ) : null}
      </span>
    </Tag>
  );
}

/**
 * 경품 수정/복사/추가 모달. 원본 prize_modify.php · prize_copy.php ·
 * product_detail_add.php 와 같은 칸 순서로, 한 줄에 칸 하나씩 둔다.
 *
 * 2026-11-04: 처음 고칠 때는 .form-stack 안에 .row-2/.row-3 로 두세 칸씩 나란히
 * 놓았는데, 그래도 "행운상 타이틀 부분이 깨진다"는 보고가 다시 와서 원본처럼 한 줄
 * 한 칸(왼쪽 제목 · 오른쪽 입력)으로 바꿨다. 칸끼리 한 줄을 나눠 쓰지 않으니 글꼴·
 * 화면 폭이 달라도 서로 겹칠 자리가 없다. 그 전(.form-grid + grid-row 고정)에는
 * 행운상 줄이 '당첨용 이미지 URL' 줄과 실제로 겹쳐 그려졌다.
 */
function PrizeFormModal({
  mode,
  lotteryId,
  productName,
  prize,
  onClose,
  onSaved,
}: {
  mode: 'edit' | 'copy' | 'add';
  lotteryId: number;
  productName: string | null;
  prize: Prize;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PrizeFormState>(() => toPrizeFormState(prize));
  const set = (key: keyof PrizeFormState) => (value: string) => setForm((f) => ({ ...f, [key]: value }));

  const [submitted, setSubmitted] = useState(false);
  /** 규칙은 서버 필드 정의(lottery/index.ts 의 LOTTERY_PRIZE_FIELDS)와 같은 값이다. */
  const errors: FieldErrors = {
    // 원본 product_detail_add.php 의 fcheck: 새로 추가할 때는 상품코드·확률을 비우면 안 된다.
    prizeCode: mode === 'add' ? checkRequired(form.prizeCode, '상품코드', 64) : checkLen(form.prizeCode, 64, '상품코드'),
    drawProbability:
      mode === 'add' && form.drawProbability.trim() === ''
        ? '당첨 확률을 입력해주세요.'
        : checkProbability(form.drawProbability),
    imageUrl: checkImageUrl(form.imageUrl),
    thumbnail: checkImageUrl(form.thumbnail),
    luckyTitle: checkLen(form.luckyTitle, 255, '행운상 타이틀'),
    description: checkLen(form.description, 5000, '설명'),
  };
  const shown: FieldErrors = submitted ? errors : {};

  const save = useMutation({
    mutationFn: () => {
      const body = toPrizeInput(form);
      return mode === 'edit' && prize.prizeId !== null
        ? lotteryApi.updatePrize(lotteryId, prize.prizeId, body)
        : lotteryApi.createPrize(lotteryId, body);
    },
    onSuccess: onSaved,
  });

  const isLucky = form.grade === 'L';
  const title = mode === 'edit' ? '경품 수정' : mode === 'copy' ? '경품 복사' : '상품 추가';
  const submitLabel = mode === 'edit' ? '수정하기' : mode === 'copy' ? '복사본 만들기' : '추가하기';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{title}</h2>

        {mode === 'copy' && (
          <p className="hint">
            <b>{text(prize.prizeCode)}</b> 경품의 내용을 그대로 새 경품으로 등록합니다. 필요하면 아래 값을
            고친 뒤 저장하세요.
          </p>
        )}
        {mode === 'add' && productName && (
          <p className="hint">
            <b>{productName}</b>에 새 경품을 추가합니다.
          </p>
        )}

        <form
          className="lottery-prize-form"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <div className="lottery-prize-form__table">
            <FormRow label="등급">
              <select
                className="input lottery-prize-form__short"
                value={form.grade}
                onChange={(e) => set('grade')(e.target.value)}
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g === 'L' ? '행운상' : `${g}상`}
                  </option>
                ))}
              </select>
            </FormRow>

            {isLucky && (
              <>
                <FormRow label="행운상 시작" as="div">
                  <input
                    className="input lottery-prize-form__date"
                    type="date"
                    aria-label="행운상 시작일"
                    value={form.luckyStartDate}
                    onChange={(e) => set('luckyStartDate')(e.target.value)}
                  />
                  <select
                    className="input lottery-prize-form__time"
                    aria-label="행운상 시작 시각"
                    value={form.luckyStartTime}
                    onChange={(e) => set('luckyStartTime')(e.target.value)}
                  >
                    {timeOptions(LUCKY_START_TIMES, form.luckyStartTime).map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </FormRow>
                <FormRow label="행운상 종료" as="div">
                  <input
                    className="input lottery-prize-form__date"
                    type="date"
                    aria-label="행운상 종료일"
                    value={form.luckyEndDate}
                    onChange={(e) => set('luckyEndDate')(e.target.value)}
                  />
                  <select
                    className="input lottery-prize-form__time"
                    aria-label="행운상 종료 시각"
                    value={form.luckyEndTime}
                    onChange={(e) => set('luckyEndTime')(e.target.value)}
                  >
                    {timeOptions(LUCKY_END_TIMES, form.luckyEndTime).map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </FormRow>
              </>
            )}

            <FormRow label="상품코드" error={shown.prizeCode}>
              <input
                className={`${inputCls(shown.prizeCode)} lottery-prize-form__short`}
                value={form.prizeCode}
                maxLength={64}
                onChange={(e) => set('prizeCode')(e.target.value)}
              />
              <span className="lottery-prize-form__hint">예: B-2 (등급-번호)</span>
            </FormRow>

            <FormRow label="확률" error={shown.drawProbability}>
              <input
                className={`${inputCls(shown.drawProbability)} lottery-prize-form__short`}
                type="text"
                inputMode="decimal"
                value={form.drawProbability}
                onChange={(e) => set('drawProbability')(e.target.value)}
              />
              <span className="lottery-prize-form__hint">%</span>
            </FormRow>

            <FormRow label="이미지" error={shown.thumbnail}>
              <input
                className={inputCls(shown.thumbnail)}
                value={form.thumbnail}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => set('thumbnail')(e.target.value)}
              />
              {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
            </FormRow>

            <FormRow label="추첨용 이미지" error={shown.imageUrl}>
              <input
                className={inputCls(shown.imageUrl)}
                value={form.imageUrl}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => set('imageUrl')(e.target.value)}
              />
            </FormRow>

            <FormRow label="수량">
              <input
                className="input lottery-prize-form__short"
                type="number"
                min={0}
                value={form.quantity}
                onChange={(e) => set('quantity')(e.target.value)}
              />
            </FormRow>

            {isLucky && (
              <FormRow label="행운상 타이틀" error={shown.luckyTitle}>
                <input
                  className={inputCls(shown.luckyTitle)}
                  value={form.luckyTitle}
                  maxLength={255}
                  onChange={(e) => set('luckyTitle')(e.target.value)}
                />
              </FormRow>
            )}

            <FormRow label="설명" error={shown.description}>
              <textarea
                className={inputCls(shown.description)}
                rows={5}
                value={form.description}
                maxLength={5000}
                onChange={(e) => set('description')(e.target.value)}
              />
            </FormRow>
          </div>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={save.isPending}>
            {save.isPending ? '저장 중…' : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
}

function HistoryPanel({
  lotteryId,
  prizeId,
  onClose,
}: {
  lotteryId: number;
  prizeId: number;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ['lottery', 'prizeHistory', lotteryId, prizeId],
    queryFn: () => lotteryApi.prizeHistory(lotteryId, prizeId),
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide lottery-prize-history" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <p className="lottery-prize-section__eyebrow">STOCK HISTORY</p>
        <h2 className="modal-title">재고 변경 이력 · 경품 #{prizeId}</h2>

        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorBox error={query.error} />
        ) : query.data.length === 0 ? (
          <EmptyBox>기록된 변경 이력이 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>작업</th>
                  <th style={{ textAlign: 'right' }}>이전</th>
                  <th style={{ textAlign: 'right' }}>이후</th>
                  <th style={{ textAlign: 'right' }}>변화</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((h, i) => {
                  const delta = (h.newQuantity ?? 0) - (h.oldQuantity ?? 0);
                  return (
                    <tr key={i}>
                      <td className="td-muted">{datetime(h.regdate)}</td>
                      <td>{text(h.action)}</td>
                      <td className="td-right">{num(h.oldQuantity)}</td>
                      <td className="td-right">{num(h.newQuantity)}</td>
                      <td className={delta < 0 ? 'td-right delta delta--down' : 'td-right delta delta--up'}>
                        {delta > 0 ? `+${delta}` : delta}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
